import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from "react-redux"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { REQUEST_ERRORS_CARDS_DEBTS } from "../../constants/requestErrorFlags"
import { showSnackBar } from "../Snackbar/Snackbar"
import { MenuItem, Select, FormControl } from "@mui/material"
import { useMyContextLoading } from "../LoadingContextProvider/LoadingContextProvider"
import { changeAccountStatusDebts, getCreditReportsStatuses } from "../../features/creditReports/creditReports"
import { LuPencil, LuCheck, LuX } from "react-icons/lu"

const AccountStatusCell = ({ creditProfileId, currentStatus }) => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const cardsDebtsOn = useSelector(selectFeatureFlag(REQUEST_ERRORS_CARDS_DEBTS))

  const [statuses, setStatuses] = useState([])
  const [selectedStatus, setSelectedStatus] = useState(currentStatus?._id || '')
  const [displayedDescription, setDisplayedDescription] = useState(currentStatus?.description || '')
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const storedStatusId = useRef(currentStatus?._id || '')

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const result = await dispatch(getCreditReportsStatuses()).unwrap()
        if (result?.data) {
          setStatuses(result.data)
        }
      } catch (e) {
        console.error("Error fetching statuses", e)
      }
    }

    if (isEditing && statuses.length === 0) {
      fetchStatuses()
    }
  }, [isEditing, dispatch, statuses.length])

  useEffect(() => {
    storedStatusId.current = currentStatus?._id || ''
    setSelectedStatus(currentStatus?._id || '')
    setDisplayedDescription(currentStatus?.description || '')
  }, [currentStatus])

  const handleSave = async () => {
    if (!selectedStatus) return

    triggerLoading(true)
    setIsLoading(true)
    if (cardsDebtsOn) {
      const chosen = statuses.find(s => s._id === selectedStatus)
      try {
        await dispatch(
          changeAccountStatusDebts({
            creditProfileId,
            data: {
              accountStatus: selectedStatus,
              statusDescription: chosen?.description
            }
          })
        ).unwrap()
        storedStatusId.current = selectedStatus
        setDisplayedDescription(chosen?.description || '')
      } catch (e) {
        showSnackBar("error", typeof e === 'string' ? e : (e?.message || "Failed to update account status"))
        setSelectedStatus(storedStatusId.current)
      }
      setIsEditing(false)
      triggerLoading(false)
      setIsLoading(false)
      return
    }
    try {
      const selectedStatusObj = statuses.find(s => s._id === selectedStatus)
      await dispatch(
        changeAccountStatusDebts({
          creditProfileId,
          data: {
            accountStatus: selectedStatus,
            statusDescription: selectedStatusObj?.description
          }
        })
      )
      setDisplayedDescription(selectedStatusObj?.description || '')
      setIsEditing(false)
    } catch (e) {
      console.error("Error saving account status", e)
    }
    triggerLoading(false)
    setIsLoading(false)
  }

  const handleCancel = () => {
    setSelectedStatus(currentStatus?._id || '')
    setIsEditing(false)
  }

  return (
    <div className="flex items-center gap-2 w-full">
      {!isEditing ? (
        <span
          className="cursor-pointer underline text-wrap line-clamp-3"
          onClick={() => setIsEditing(true)}
        >
          {displayedDescription || <LuPencil size={16} className="text-text-secondary" />}
        </span>
      ) : (
        <div className="flex items-center gap-1 w-full">
          <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              disabled={isLoading}
              displayEmpty
              sx={{
                fontSize: '12px',
                '.MuiSelect-select': {
                  padding: '4px 8px',
                },
                'fieldset': {
                  borderColor: 'var(--color-divider)',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--color-primary-main) !important',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--color-primary-main) !important',
                  borderWidth: '1px !important',
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 250,
                    bgcolor: 'var(--color-bg-paper)',
                    border: '1px solid var(--color-divider)',
                  }
                }
              }}
            >
              <MenuItem value="" disabled>
                <em>Select status</em>
              </MenuItem>
              {statuses.map((status) => (
                <MenuItem
                  key={status._id}
                  value={status._id}
                  sx={{ fontSize: '12px' }}
                >
                  {status.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className="flex flex-col items-center justify-between h-fit gap-1">
            <button onClick={handleSave} disabled={isLoading || !selectedStatus}>
              <LuCheck size={18} className="text-success-main" />
            </button>
            <button onClick={handleCancel} disabled={isLoading}>
              <LuX size={18} className="text-text-secondary" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AccountStatusCell
